import Alert from '@amzn/awsui-components-core/alert';
import Badge from '@amzn/awsui-components-core/badge';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import ContentLayout from '@amzn/awsui-components-core/content-layout';
import Header from '@amzn/awsui-components-core/header';
import HelpPanel from '@amzn/awsui-components-core/help-panel';
import Link from '@amzn/awsui-components-core/link';
import Select, { SelectProps } from '@amzn/awsui-components-core/select';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import Spinner from '@amzn/awsui-components-core/spinner';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useStarGridData } from '../context/StarGridDataContext';
import { useStarGridPnlReport } from '../hooks/useStarGridPnlReport';
import PnlReportGrid from '../components/PnlReportGrid';
import ReportHealth from '../components/ReportHealth';
import { PNL_DEFAULT_SCALE, PNL_SCALE_OPTIONS } from '../utils/pnlGrid';
import styles from './styles/ReportDetailView.module.scss';

export interface ReportDetailViewProps {
  reportId: number;
  /** Called after the report is deleted (parent refreshes the list + navigates away). */
  onDeleted: () => void;
  /** Called when the user clicks "Back to Reports" — parent routes to the home view. */
  onBack?: () => void;
}

const SCALE_OPTIONS: SelectProps.Options = PNL_SCALE_OPTIONS.map((s) => ({ value: s.id, label: s.label }));

/**
 * A created PnL report's detail view. Loads the saved definition, runs its pivot query,
 * and renders the scenario × period grid (tree-drillable, finance-formatted). The flat
 * long serving result is pivoted to wide client-side (see hooks/useStarGridPnlReport).
 */
export const ReportDetailView: React.FC<ReportDetailViewProps> = ({ reportId, onDeleted, onBack }) => {
  const { pnlReports, pnlConfig, setTools, setToolsOpen } = useStarGridData();
  const [scaleId, setScaleId] = useState<string>(PNL_DEFAULT_SCALE.id);

  // Load the picker config once — needed for human dimension labels on the pivot header.
  useEffect(() => {
    pnlConfig.ensureLoaded();
  }, [pnlConfig]);

  const dimensionLabels = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    for (const d of pnlConfig.data?.available_dimensions ?? []) {
      const key = d.fact_key ?? d.hierarchy_id;
      if (key && d.label) map[key] = d.label;
    }
    return map;
  }, [pnlConfig.data]);

  const onReportDeleted = useCallback(() => {
    pnlReports.refresh();
    onDeleted();
  }, [pnlReports, onDeleted]);

  const { definition, model, response, loading, error, oversized, groupBy, drillableDimensions, reload, drillDown } = useStarGridPnlReport(
    reportId,
    onReportDeleted
  );

  const selectedScale = useMemo<SelectProps.Option>(() => SCALE_OPTIONS.find((o) => o.value === scaleId) ?? SCALE_OPTIONS[0], [scaleId]);

  // ─── Tool Panel content ──────────────────────────────────────────────────────
  // Report Health & Stats block (ported from xPT PnlReportHealth) + the report's
  // definition + generated SQL. Rebuilt whenever the config or the last response
  // changes; cleared on unmount so other views don't inherit this one's panel.
  const infoPanel = useMemo(
    () => (
      <HelpPanel header={<h2>Report Health & Stats</h2>}>
        {definition ? <ReportHealth config={definition} /> : <Box color="text-body-secondary">Loading report health…</Box>}

        {definition ? (
          <>
            <div style={{ borderTop: '1px solid var(--color-border-divider-default, #d5dbdb)', margin: '16px 0' }} />
            <h3 style={{ marginTop: 0 }}>Definition</h3>
            <div style={{ marginBottom: 8 }}>
              <Box variant="awsui-key-label">Fact source</Box>
              <Badge>{definition.source_mode === 'EXTERNAL' ? 'External (cdp_lake Glue)' : 'Native (Ingested)'}</Badge>
            </div>
            <div style={{ marginBottom: 8 }}>
              <Box variant="awsui-key-label">{`Drillable dimensions (${(definition.group_by_dimensions ?? []).length})`}</Box>
              <SpaceBetween direction="horizontal" size="xxs">
                {(definition.group_by_dimensions ?? []).map((d) => (
                  <Badge key={d}>{d}</Badge>
                ))}
              </SpaceBetween>
            </div>
            <div style={{ marginBottom: 8 }}>
              <Box variant="awsui-key-label">{`Filter dimensions (${(definition.filter_by_dimension ?? []).length})`}</Box>
              {(definition.filter_by_dimension ?? []).length > 0 ? (
                <SpaceBetween direction="horizontal" size="xxs">
                  {(definition.filter_by_dimension ?? []).map((d) => (
                    <Badge key={d}>{d}</Badge>
                  ))}
                </SpaceBetween>
              ) : (
                <Box color="text-body-secondary">None.</Box>
              )}
            </div>
            <div style={{ marginBottom: 8 }}>
              <Box variant="awsui-key-label">{`Scenarios (${(definition.scenarios ?? []).length})`}</Box>
              <SpaceBetween size="xxs">
                {(definition.scenarios ?? []).map((s, i) => (
                  <Box key={`${s.reporting_scenario_id}-${i}`}>
                    <Badge>{s.label}</Badge> · {s.months.length} months
                  </Box>
                ))}
              </SpaceBetween>
            </div>
          </>
        ) : null}

        {response?.sql ? (
          <>
            <div style={{ borderTop: '1px solid var(--color-border-divider-default, #d5dbdb)', margin: '16px 0' }} />
            <h3 style={{ marginTop: 0 }}>Generated SQL</h3>
            <Box variant="code">{response.sql}</Box>
          </>
        ) : null}
      </HelpPanel>
    ),
    [definition, response]
  );

  useEffect(() => {
    setTools(infoPanel);
    return () => setTools(null);
  }, [infoPanel, setTools]);

  // Auto-poll while the backing MV is still refreshing so the pivot appears without a
  // manual refresh click. Stops firing the moment status flips to READY / FAILED.
  useEffect(() => {
    if (!definition || definition.status === 'READY') return undefined;
    if (definition.status === 'FAILED' || definition.status === 'DELETED') return undefined;
    const handle = window.setInterval(() => reload(), 5000);
    return () => window.clearInterval(handle);
  }, [definition, reload]);

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={definition?.report_description ?? undefined}
          info={
            <Link variant="info" onFollow={() => setToolsOpen(true)}>
              Info
            </Link>
          }
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button iconName="refresh" onClick={reload} loading={loading}>
                Refresh
              </Button>
              <Button iconName="arrow-left" onClick={onBack} disabled={!onBack}>
                Back to Reports
              </Button>
            </SpaceBetween>
          }
        >
          {definition?.report_name ?? `Report ${reportId}`}
        </Header>
      }
    >
      <SpaceBetween size="l">
        {error && (
          <Alert type="error" header="Could Not Load Report">
            {error}
          </Alert>
        )}

        {definition?.status === 'GENERATING' && (
          <Alert type="info" header="Report Is Generating">
            <SpaceBetween size="xs">
              <Box>The backing materialized view is still refreshing. This page will refresh automatically once the first pass completes.</Box>
              {definition.mv_refresh_state ? (
                <Box variant="small" color="text-body-secondary">
                  StarRocks state: <code>{definition.mv_refresh_state}</code>
                  {definition.mv_last_refresh_duration ? <> · duration: {definition.mv_last_refresh_duration}s</> : null}
                </Box>
              ) : null}
            </SpaceBetween>
          </Alert>
        )}

        {definition?.status === 'FAILED' && (
          <Alert type="error" header="Report Generation Failed">
            {definition.mv_last_refresh_error || 'The backing MV refresh failed. Delete and recreate, or retry via Refresh.'}
          </Alert>
        )}

        {oversized ? (
          <Alert type="warning" header="Result Too Large">
            <SpaceBetween size="xs">
              <Box>
                This drill would return approximately <strong>{oversized.estimatedRows.toLocaleString()}</strong> rows — over the{' '}
                <strong>{oversized.rowLimit.toLocaleString()}</strong>-row guardrail.
              </Box>
              <Box variant="small" color="text-body-secondary">
                {oversized.hint}
              </Box>
            </SpaceBetween>
          </Alert>
        ) : null}

        {loading && !model ? (
          <Box textAlign="center" padding="l">
            <Spinner /> Running report…
          </Box>
        ) : (
          model && (
            <SpaceBetween size="s">
              <div className={styles.scaleToolbar}>
                <span className={styles.scaleLabel}>Scale</span>
                <div className={styles.scaleControl}>
                  <Select
                    selectedOption={selectedScale}
                    options={SCALE_OPTIONS}
                    onChange={({ detail }) => setScaleId(detail.selectedOption.value ?? PNL_DEFAULT_SCALE.id)}
                    data-testid="stargrid-report-scale-select"
                  />
                </div>
              </div>

              <div className={styles.gridFrame}>
                <PnlReportGrid
                  model={model}
                  loading={loading}
                  scaleId={scaleId}
                  groupBy={groupBy}
                  drillableDimensions={drillableDimensions}
                  onDrillDown={drillDown}
                  dimensionLabels={dimensionLabels}
                />
              </div>
            </SpaceBetween>
          )
        )}
      </SpaceBetween>
    </ContentLayout>
  );
};

export default ReportDetailView;
